import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, setPersistence, indexedDBLocalPersistence, browserLocalPersistence, Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';

// ... (existing code)

// Your Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ];

  const missingFields = requiredFields.filter(field => !firebaseConfig[field as keyof typeof firebaseConfig]);

  if (missingFields.length > 0) {
    console.error('❌ Firebase configuration missing required fields:', missingFields);
    return false;
  }

  console.log('✅ Firebase configuration validated successfully');
  return true;
};

// Initialize Firebase with error handling
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let analytics: Analytics | null = null;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app);
  
  // Initialize Google Analytics (solo si está soportado en el navegador)
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('✅ Google Analytics inicializado');
    } else {
      console.log('⚠️ Google Analytics no soportado en este navegador');
    }
  }).catch((error) => {
    console.warn('⚠️ Error inicializando Google Analytics:', error);
  });

  // Configurar persistencia LOCAL para que la sesión se mantenga al cerrar la app (PWA)
  // indexedDBLocalPersistence es mejor para PWA, con fallback a browserLocalPersistence
  setPersistence(auth, indexedDBLocalPersistence)
    .then(() => {
      console.log('✅ Firebase Auth configurado con persistencia LOCAL (IndexedDB)');
    })
    .catch((error) => {
      console.warn('⚠️ IndexedDB no disponible, usando localStorage:', error);
      // Fallback a localStorage
      return setPersistence(auth, browserLocalPersistence);
    })
    .then(() => {
      console.log('✅ Firebase Auth persistencia configurada correctamente');
    })
    .catch((error) => {
      console.error('❌ Error configurando persistencia de Firebase Auth:', error);
    });

  console.log('✅ Firebase services initialized successfully');
} catch (error) {
  console.error('❌ Error initializing Firebase:', error);
  throw error;
}

// Export services
export { auth, db, storage, functions, analytics };

// Simple connection manager without complex operations
export const firebaseConnectionManager = {
  isOnline: true,
  retryCount: 0,
  maxRetries: 3,

  async checkConnection() {
    try {
      // Simple connection check without complex operations
      this.isOnline = true;
      this.retryCount = 0;
      console.log('✅ Firebase connection successful');
      return true;
    } catch (error) {
      console.error('❌ Firebase connection failed:', error);
      this.isOnline = false;
      return false;
    }
  },

  async withRetry<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        this.retryCount = attempt;
        console.log(`🔄 Attempt ${attempt} of ${this.maxRetries}...`);

        const result = await operation();
        this.retryCount = 0;
        return result;
      } catch (error) {
        lastError = error;
        console.error(`❌ Attempt ${attempt} failed:`, error);

        if (attempt < this.maxRetries) {
          console.log(`⏳ Waiting ${attempt * 2} seconds before retry...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 2000));
        }
      }
    }

    throw lastError;
  }
};

// Connect to emulators in development mode
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('🔧 Connected to Firebase emulators');
  } catch (error) {
    console.warn('⚠️ Could not connect to Firebase emulators:', error);
  }
}

// Validate configuration on startup
validateFirebaseConfig();

export default app; 